/**
 * NCR Detail — Section 1: Customer & Supplier.
 *
 * Extracted verbatim from NCRDetailTab for the 500-line file-size rule.
 */

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { NonConformanceReport } from '../../../lib/api';
import { NCREditFormData } from '../types';
import { Section } from '../Section';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { ncrHasSupplier, ncrSupplierLabel } from '../../../components/quality/ncrNoSupplier';

interface CustomerSupplierSectionProps {
  ncr: NonConformanceReport;
  formData: NCREditFormData;
  canEdit: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
}

export function CustomerSupplierSection({
  ncr,
  formData,
  canEdit,
  onUpdateFormData,
}: CustomerSupplierSectionProps) {
  const { formatDate } = useFormatters();

  return (
    <Section title="Customer & Supplier">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Customer</Label>
          <Input value={ncr.company_name} disabled />
        </div>
        <div>
          <Label>Supplier</Label>
          {/* Display-only: the supplier is fixed at creation. `readOnly`
              keeps React from warning about a value with no onChange once
              the field is enabled on a draft. */}
          <Input value={ncrSupplierLabel(ncr)} readOnly disabled={!canEdit} />
          {!ncrHasSupplier(ncr) && (
            <p className="mt-1 text-xs text-muted-foreground">
              Internal reject: no supplier sees this NCR and it does not count towards any supplier score.
            </p>
          )}
        </div>
        <div>
          <Label>Raised By</Label>
          <Input value={ncr.raised_by_name} disabled />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          {/* B3 (Aaron, 25 Sep 2026): the NCR number is always generated; the
              customer's own number has its own box. An older NCR whose number
              was typed over keeps showing it here. */}
          <Label>NCR No.</Label>
          <Input value={ncr.ncr_number || '—'} disabled />
        </div>
        <div>
          {/* E4: named as this NCR's customer names it. */}
          <Label htmlFor="customer_reference">{ncr.customer_reference_label ?? 'Customer NCR No.'}</Label>
          <Input
            id="customer_reference"
            value={(formData.customer_reference as string | undefined) ?? ncr.customer_reference ?? ''}
            onChange={(e) => onUpdateFormData('customer_reference', e.target.value)}
            placeholder={canEdit ? 'Optional: your own reference' : ''}
            disabled={!canEdit}
          />
        </div>
        <div>
          <Label>NCR Date</Label>
          <Input value={ncr.ncr_date ? formatDate(ncr.ncr_date) : '—'} disabled />
        </div>
        <div>
          <Label>Severity</Label>
          <select
            value={formData.severity || 'minor'}
            onChange={(e) => onUpdateFormData('severity', e.target.value)}
            disabled={!canEdit}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          >
            <option value="minor">Minor</option>
            <option value="major">Major</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>
    </Section>
  );
}
